import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export type SpaLang = 'ru' | 'kk' | 'en';

/**
 * Lightweight i18n: fetches /auth-spa/i18n/{lang}.json on demand and exposes a
 * t(key, args) translator. Keys mirror messages_*.properties.
 */
@Injectable({ providedIn: 'root' })
export class I18nService {
  private http = inject(HttpClient);
  private cache = new Map<SpaLang, Record<string, string>>();
  private dict = signal<Record<string, string>>({});
  private currentLang = signal<SpaLang>('ru');

  readonly lang = this.currentLang.asReadonly();

  async use(lang: SpaLang): Promise<void> {
    const safe: SpaLang = lang === 'kk' || lang === 'en' ? lang : 'ru';
    if (!this.cache.has(safe)) {
      // Use document.baseURI so the URL resolves against <base href> from
      // index.html: in prod this points to https://cdn.uchitelya.kz/auth-spa/,
      // in dev it's /auth-spa/. JSON bundles are uploaded next to JS chunks,
      // so this works without code branching.
      const url = new URL(`i18n/${safe}.json`, document.baseURI).toString();
      const data = await firstValueFrom(
        this.http.get<Record<string, string>>(url),
      );
      this.cache.set(safe, data);
    }
    this.currentLang.set(safe);
    this.dict.set(this.cache.get(safe)!);
    document.documentElement.lang = safe === 'kk' ? 'kk' : safe;
  }

  /** `t('login.error.email_not_confirmed')`; placeholders `{0}` etc. */
  t(key: string, args?: ReadonlyArray<string | number>): string {
    let value = this.dict()[key] ?? key;
    if (args && args.length) {
      for (let i = 0; i < args.length; i++) {
        value = value.replace(new RegExp(`\\{${i}\\}`, 'g'), String(args[i]));
      }
    }
    return value;
  }

  /** Map URL prefix `ru` / `kz` to message-bundle code (`ru` / `kk`). */
  static normaliseFromUrl(raw: string | null | undefined): SpaLang {
    if (raw === 'kz' || raw === 'kk') return 'kk';
    if (raw === 'en') return 'en';
    return 'ru';
  }
}
